// Asset segmentation (AMB-369/370 / E, module 5).
//
// Generation wraps each editable asset in a typed tag: <asset type="email" label="V1">…
// </asset>. This module parses those tags so the editor can scope editing to the asset
// block(s) — N tags → N editors — while the surrounding scaffolding (Recommendation, Open
// questions, instructions) stays read-only. A body with no tags is "legacy" and edits whole.
//
// Pure string work, no DOM — usable by the editor, the markdown render step, and the backfill.

export interface AssetBlock {
  type: string;
  label: string;
  content: string;
  platform: string; // optional <asset … platform="google|meta|facebook|instagram|linkedin"> — drives the native preview frame
}

export interface SplitResult {
  hasAssets: boolean;
  assets: AssetBlock[];
}

// One <asset …>…</asset> block. Non-greedy body so adjacent assets don't merge; [\s\S] so
// the body can span newlines and contain code fences (ce-entry).
const ASSET_RE = /<asset\b([^>]*)>([\s\S]*?)<\/asset>/g;

const attr = (attrs: string, name: string): string => {
  const m = new RegExp(`${name}\\s*=\\s*"([^"]*)"`).exec(attrs);
  return m ? m[1] : "";
};

export function splitAssets(body: string): SplitResult {
  const assets: AssetBlock[] = [];
  const re = new RegExp(ASSET_RE);
  let m: RegExpExecArray | null;
  while ((m = re.exec(body || "")) !== null) {
    assets.push({ type: attr(m[1], "type"), label: attr(m[1], "label"), content: m[2].trim(), platform: attr(m[1], "platform") });
  }
  return { hasAssets: assets.length > 0, assets };
}

// An ordered walk of the body: scaffolding text and asset blocks in the sequence they appear,
// so a renderer can show scaffolding (read-only context) and assets (typed) inline + in order.
export type Segment =
  | { kind: "scaffold"; text: string }
  | { kind: "asset"; type: string; label: string; content: string; index: number; platform: string };

export function splitSegments(body: string): Segment[] {
  const segs: Segment[] = [];
  const re = new RegExp(ASSET_RE);
  const src = body || "";
  let last = 0; let i = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) {
      const text = src.slice(last, m.index);
      if (text.trim()) segs.push({ kind: "scaffold", text });
    }
    segs.push({ kind: "asset", type: attr(m[1], "type"), label: attr(m[1], "label"), content: m[2].trim(), index: i++, platform: attr(m[1], "platform") });
    last = m.index + m[0].length;
  }
  if (last < src.length) {
    const text = src.slice(last);
    if (text.trim()) segs.push({ kind: "scaffold", text });
  }
  return segs;
}

/**
 * Replace the inner content of the index-th asset, keeping its wrapper tag (and every other
 * asset) byte-for-byte. No-op if the index is out of range.
 */
export function replaceAsset(body: string, index: number, newContent: string): string {
  if (index < 0) return body;
  let i = 0;
  return (body || "").replace(ASSET_RE, (full, attrs, _content) => {
    const out = i === index ? `<asset${attrs}>${newContent}</asset>` : full;
    i++;
    return out;
  });
}
